import { Injectable } from '@angular/core';
import * as Stigg from '@stigg/js-client-sdk';
import { toDictionary } from '@anecdotes/utils';
import { BehaviorSubject, Observable, filter, firstValueFrom, map, merge, timeout } from 'rxjs';
import { FeatureModel } from '../../models';
import { StiggClientWrapper } from '../stigg-client-wrapper/stigg-client-wrapper';
import { FeatureTypeEnum } from '../../models/feature-model';

const FEATURE_METERED_KEYS = [
  'feature-user-grc-owner',
  'feature-internalUsers',
  'feature-standard-fws',
  'feature-custom-fws',
  'feature-cloud-accounts',
  'feature-plugins',
  'feature-feature-datainfra-uda',
  'feature-user-collaborator',
  'feature-user-free',
];
const FEATURE_BOOLEAN_KEYS = [
  'feature-uam-app',
  'feature-uar-entities',
  'feature-auditLogs',
  'feature-connected-app',
  'feature-customRoles',
  'feature-dataDelegation',
  'feature-onPerm-plugins',
  'feature-policyManager',
  'feature-riskManager',
  'feature-scim',
  'feature-userAccess',
  'feature-trustCenter',
  'feature-risks-entities',
  'feature-playbooks-app',
  'feature-sigma-premium',
  'feature-evidence-views-p-2',
  'feature-merged-evidence',
  'feature-nql-ai',
  'feature-document-ai-analysis',
  'feature-smart-search-ai',
  'feature-evidence-builder',
  'feature-evidence-builder-ai-suggestions',
  'feature-premium-support',
  'feature-ai-consent',
];

interface FeaturesDictionary {
  [featureId: string]: FeatureModel;
}

@Injectable()
export class FeaturesService {
  private isInitializationCalled = false;
  private allEntitlements$ = new BehaviorSubject<FeaturesDictionary>(null);

  constructor(private stiggClientWrapper: StiggClientWrapper) {}

  async initAsync(): Promise<void> {
    if (this.isInitializationCalled) {
      return;
    }

    this.isInitializationCalled = true;

    try {
      const allEntitlements$ = this.stiggClientWrapper.getAllEntitlements().pipe(filter(Boolean));

      allEntitlements$
        .pipe(
          map((entitlements) => this.createEntitlementsDictionary(entitlements)),
          map((existingFeatures) => this.consolidateExistingFeaturesWithFallbacks(existingFeatures))
        )
        .subscribe((features) => this.allEntitlements$.next(features));

      // This code will throw error if stigg does not init within 4 seconds
      // and hence the code will use fallback features that will be enabled
      await firstValueFrom(
        merge(
          this.stiggClientWrapper.initAsync(),
          allEntitlements$.pipe(timeout(4000)),
        ),
      );
    } catch (err) {
      // we need to throw this error to console anyway in order to see it in DD
      // eslint-disable-next-line no-console
      console.error(err);
      // This will fallback all known features to hasAccess: true
      this.allEntitlements$.next(this.getFallbackFeaturesDictionary());
    }
  }

  /**
   * Returns entitlement for provided feature.
   * NOTE: The code should ONLY rely on hasAccess property for all feature types.
   * Other properties are just informational.
   * @param featureId
   * @returns
   */
  getEntitlement(featureId: string): Observable<FeatureModel> {
    return this.allEntitlements$.pipe(
      filter(Boolean),
      map(() => this.getEntitlementSync(featureId)),
    );
  }

  /**
   * Returns entitlement for provided feature.
   * NOTE: The code should ONLY rely on hasAccess property for all feature types.
   * Other properties are just informational.
   * @param featureId
   * @returns
   */
  getEntitlementSync(featureId: string): FeatureModel {
    if (this.allEntitlements$.value && this.allEntitlements$.value[featureId]) {
      return this.allEntitlements$.value[featureId];
    }

    return { featureId: featureId, hasAccess: false } as FeatureModel;
  }

  /**
   * Returns all entitlements for all features.
   * NOTE: The code should ONLY rely on hasAccess property for all feature types.
   * Other properties are just informational.
   * @returns
   */
  getEntitlements(): Observable<FeatureModel[]> {
    return this.allEntitlements$.pipe(filter(Boolean), map((allEntitlements) => Object.values(allEntitlements)));
  }

  reloadEntitlements(): void {
    this.stiggClientWrapper.reloadEntitlements();
  }

  private createEntitlementsDictionary(entitlements: Stigg.Entitlement[]): { [featureId: string]: FeatureModel } {
    const allFeatures = entitlements.map((entitlement) =>
      this.mapFeature(entitlement.feature.id as string, entitlement as Stigg.MeteredEntitlement),
    );

    return toDictionary(
      allFeatures,
      (feature) => feature.featureId,
      (feature) => feature,
    );
  }

  private mapFeature(featureId: string, entitlement: Stigg.Entitlement): FeatureModel {
    const feature: FeatureModel = {
      featureId: featureId,
      units: entitlement.feature?.units,
      unitsPlural: entitlement.feature?.unitsPlural,
      hasAccess: entitlement.hasAccess,
      featureType: FeatureTypeEnum.Boolean
    };

    if (entitlement.feature.featureType === Stigg.FeatureType.Number) {
      const meteredEntitlement = entitlement as Stigg.MeteredEntitlement;
      feature.usageLimit = meteredEntitlement.usageLimit;
      feature.currentUsage = meteredEntitlement.currentUsage;
      feature.isUnlimited = meteredEntitlement.isUnlimited;
      feature.featureType = FeatureTypeEnum.Metered;
      const remainedUsage = feature.usageLimit - feature.currentUsage;

      if (remainedUsage >= 0) {
        feature.remainedUsage = remainedUsage;
      }
    }

    return feature;
  }

  private getFallbackFeaturesDictionary(): FeaturesDictionary {
    const allFallbackFeatures = [
      ...FEATURE_METERED_KEYS.map((featureId) => ({
        featureId: featureId,
        hasAccess: true,
        isUnlimited: true,
        currentUsage: 0,
        usageLimit: 0,
        featureType: FeatureTypeEnum.Metered
      } as FeatureModel)),
      ...FEATURE_BOOLEAN_KEYS.map((featureId) => ({
        featureId: featureId,
        hasAccess: true,
        featureType: FeatureTypeEnum.Boolean
      } as FeatureModel))
    ];

    return toDictionary(allFallbackFeatures, (feature) => feature.featureId) as FeaturesDictionary;
  }

  private consolidateExistingFeaturesWithFallbacks(existingFeatures: FeaturesDictionary): FeaturesDictionary {
    const result = { ...existingFeatures };
    const fallbackFeatures = this.getFallbackFeaturesDictionary();
    Object.values(fallbackFeatures).forEach((feature) => {
      feature.hasAccess = false;
      feature.isUnlimited = false;
    });
    Object.entries(fallbackFeatures).filter(([key]) => !result[key]).forEach(([key, value]) => result[key] = value);
    return result;
  }
}
